import type {
  AuditUserInfo,
  ColumnAccessColumnItem,
  ColumnAccessPermissionLevel,
  ColumnAccessPermissionsResponse,
  ColumnAccessUserItem,
  ReplaceColumnAccessPermissionsPayload,
} from 'src/shared/api/table/types';

export type ColumnAccessDraftPermission = {
  user_id: number;
  level: ColumnAccessPermissionLevel;
};

export type ColumnAccessDraftColumn = Omit<ColumnAccessColumnItem, 'permissions'> & {
  permissions: ColumnAccessDraftPermission[];
};

export type ColumnAccessDraftState = Omit<ColumnAccessPermissionsResponse, 'columns'> & {
  columns: ColumnAccessDraftColumn[];
};

export const resolvePermissionLevel = (
  canRead: boolean,
  canEdit: boolean,
): ColumnAccessPermissionLevel => {
  if (canEdit) {
    return 'edit';
  }

  if (canRead) {
    return 'read';
  }

  return 'none';
};

export const resolvePermissionFlags = (
  level: ColumnAccessPermissionLevel,
): { can_read: boolean; can_edit: boolean } => {
  if (level === 'edit') {
    return {
      can_read: true,
      can_edit: true,
    };
  }

  if (level === 'read') {
    return {
      can_read: true,
      can_edit: false,
    };
  }

  return {
    can_read: false,
    can_edit: false,
  };
};

export const resolveBasePermissionLevel = (
  user?: ColumnAccessUserItem | null,
): ColumnAccessPermissionLevel => {
  return resolvePermissionLevel(Boolean(user?.form_can_read), Boolean(user?.form_can_edit));
};

const permissionLevelRank: Record<ColumnAccessPermissionLevel, number> = {
  none: 0,
  read: 1,
  edit: 2,
};

const rolePermissionCaps: Partial<Record<string, ColumnAccessPermissionLevel>> = {
  admin: 'edit',
  developer: 'edit',
  form_designer: 'edit',
  form_operator: 'edit',
  operator: 'edit',
  data_operator: 'edit',
  viewer: 'read',
  report_viewer: 'read',
};

const minPermissionLevel = (
  left: ColumnAccessPermissionLevel,
  right: ColumnAccessPermissionLevel,
): ColumnAccessPermissionLevel => {
  return permissionLevelRank[left] <= permissionLevelRank[right] ? left : right;
};

export const resolveRolePermissionCap = (
  user?: ColumnAccessUserItem | null,
): ColumnAccessPermissionLevel => {
  const normalizedRole = user?.role?.trim().toLowerCase() || '';
  return rolePermissionCaps[normalizedRole] || 'edit';
};

export const resolveAllowedPermissionLevel = (
  user?: ColumnAccessUserItem | null,
): ColumnAccessPermissionLevel => {
  return minPermissionLevel(resolveBasePermissionLevel(user), resolveRolePermissionCap(user));
};

export const clampColumnPermissionLevel = (
  user: ColumnAccessUserItem | null | undefined,
  level: ColumnAccessPermissionLevel,
): ColumnAccessPermissionLevel => {
  return minPermissionLevel(level, resolveAllowedPermissionLevel(user));
};

export const toggleColumnReadPermission = (
  user: ColumnAccessUserItem | null | undefined,
  currentLevel: ColumnAccessPermissionLevel,
  checked: boolean,
): ColumnAccessPermissionLevel => {
  if (!checked) {
    return 'none';
  }
  if (currentLevel === 'edit') {
    return clampColumnPermissionLevel(user, 'edit');
  }
  return clampColumnPermissionLevel(user, 'read');
};

export const toggleColumnEditPermission = (
  user: ColumnAccessUserItem | null | undefined,
  currentLevel: ColumnAccessPermissionLevel,
  checked: boolean,
): ColumnAccessPermissionLevel => {
  if (checked) {
    return clampColumnPermissionLevel(user, 'edit');
  }
  if (currentLevel === 'edit') {
    return clampColumnPermissionLevel(user, 'read');
  }
  return clampColumnPermissionLevel(user, currentLevel);
};

export const formatColumnAccessUserDisplayName = (user?: AuditUserInfo | null): string => {
  const displayName = user?.display_name?.trim();
  if (displayName) {
    return displayName;
  }

  const fullName = user?.full_name?.trim();
  if (fullName) {
    return fullName;
  }

  const userName = user?.user_name?.trim();
  if (userName) {
    return userName;
  }

  const email = user?.email?.trim();
  if (email) {
    return email;
  }

  if (typeof user?.id === 'number' && user.id > 0) {
    return `ID ${user.id}`;
  }

  return 'Unknown user';
};

export const buildColumnAccessDraft = (
  response: ColumnAccessPermissionsResponse,
): ColumnAccessDraftState => {
  const usersById = new Map(response.users.map(user => [user.id, user]));

  return {
    ...response,
    columns: response.columns.map(column => ({
      ...column,
      permissions: column.permissions.map(permission => ({
        user_id: permission.user_id,
        level: clampColumnPermissionLevel(
          usersById.get(permission.user_id),
          resolvePermissionLevel(permission.can_read, permission.can_edit),
        ),
      })),
    })),
  };
};

export const buildColumnAccessPayload = (
  draft: ColumnAccessDraftState,
): ReplaceColumnAccessPermissionsPayload => {
  const usersById = new Map(draft.users.map(user => [user.id, user]));

  return {
    columns: draft.columns.map(column => ({
      column_name: column.column_name,
      permissions: column.permissions
        .filter(permission => {
          const baseLevel = resolveBasePermissionLevel(usersById.get(permission.user_id));
          return permission.level !== baseLevel;
        })
        .map(permission => ({
          user_id: permission.user_id,
          ...resolvePermissionFlags(permission.level),
        })),
    })),
  };
};

export const haveColumnAccessChanges = (
  initialDraft: ColumnAccessDraftState | null,
  currentDraft: ColumnAccessDraftState | null,
): boolean => {
  if (!initialDraft || !currentDraft) {
    return false;
  }

  return (
    JSON.stringify(buildColumnAccessPayload(initialDraft)) !==
    JSON.stringify(buildColumnAccessPayload(currentDraft))
  );
};
